import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import type { DirectedStyle } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { storyMessages as messages } from '../messages/storyMessages';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { isOwnStyleValid } from './director/choiceRules';
import { EffectFamilyChips } from './director/EffectFamilyChips';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

type TextField = 'camera' | 'colourAndLight' | 'texture' | 'pacing';

const FIELDS: ReadonlyArray<{ key: TextField; label: MessageDescriptor }> = [
  { key: 'camera', label: messages.camera },
  { key: 'colourAndLight', label: messages.colourAndLight },
  { key: 'texture', label: messages.texture },
  { key: 'pacing', label: messages.pacing },
];

interface StyleEditorProps {
  style: DirectedStyle;
  /** "Your own" or "Style 2". */
  label: string;
  onChange: (style: DirectedStyle) => void;
  onDone: () => void;
  onCancel: () => void;
}

/**
 * The five fields of a style and its effect families (design d2): "Write my own", and Edit of a
 * style of the director (the same fields, filled with its text).
 */
const StyleEditorComponent: React.FC<StyleEditorProps> = ({ style, label, onChange, onDone, onCancel }) => {
  const intl = useIntl();
  const { named, toIds } = useDirectedCastNames();
  const isValid = isOwnStyleValid(style);
  return (
    <div className={clsx(styles.option, styles.optionOn)} data-testid={`style-editor-${style.id}`}>
      <div className={styles.optionHead}>
        <span className={styles.optionNumber}>{label}</span>
        <i className={clsx(styles.radio, styles.radioOn)} aria-hidden />
      </div>
      <label className={styles.fieldLabel} htmlFor={`style-name-${style.id}`}>
        {intl.formatMessage(messages.name)}
      </label>
      <input
        id={`style-name-${style.id}`}
        className={clsx(styles.input, styles.inputTitle)}
        value={named(style.title)}
        onChange={(event) => onChange({ ...style, title: toIds(event.target.value) })}
      />
      {FIELDS.map((field) => (
        <React.Fragment key={field.key}>
          <label className={styles.fieldLabel} htmlFor={`style-${field.key}-${style.id}`}>
            {intl.formatMessage(field.label)}
          </label>
          <textarea
            id={`style-${field.key}-${style.id}`}
            className={styles.input}
            rows={2}
            value={named(style[field.key])}
            onChange={(event) => onChange({ ...style, [field.key]: toIds(event.target.value) })}
          />
        </React.Fragment>
      ))}
      <span className={styles.fieldLabel}>{intl.formatMessage(messages.effectsToLean)}</span>
      <EffectFamilyChips
        families={style.effectFamilies}
        label={intl.formatMessage(messages.effectsToLean)}
        withHints
        onChange={(effectFamilies) => onChange({ ...style, effectFamilies })}
      />
      {!isValid && <p className={styles.optionText}>{intl.formatMessage(messages.ownInvalid)}</p>}
      <div className={styles.optionActions}>
        <SMLButton variant="primary" className={styles.small} disabled={!isValid} onClick={onDone}>
          {intl.formatMessage(messages.done)}
        </SMLButton>
        <SMLButton className={clsx(styles.small, styles.ghost)} onClick={onCancel}>
          {intl.formatMessage(messages.cancel)}
        </SMLButton>
      </div>
    </div>
  );
};

export const StyleEditor = TypedMemo(StyleEditorComponent);
